import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureNames } from '@office-start/telemetry-types';

import { BottomTrayComponent } from '../bottom-tray/bottom-tray.component';
import { RoutePaths } from '../shared/constants/route-paths.constant';
import { LocalStorageService } from '../shared/services/local-storage.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { BaseRouteGuard } from './base.route-guard';

const STICKY_PAGE_TIMEOUT = 6000; // 6s which matches the P95 load time.
const STICKY_PAGE_REDIRECT_EVENT_NAME = 'StickyPageSettingRedirect';
const STICKY_PAGE_FETCH_EVENT_NAME = 'StickyPageSettingFetch';

@Injectable({
    providedIn: 'root'
})
export class HomeRouteGuard extends BaseRouteGuard implements CanActivate {
    constructor(
        router: Router,
        capabilitiesService: CapabilitiesService,
        private readonly localStorageService: LocalStorageService
    ) {
        super(router, capabilitiesService);
    }

    public async canActivate(route: ActivatedRouteSnapshot) {
        if (this.capabilitiesService.capabilities.bottomTrayEnabled) {
            const lastTab = this.getStickyTab();

            return lastTab
                ? this.checkEnabledAndRedirect(lastTab === RoutePaths.OfficeHomeFallback, lastTab, route.queryParams)
                : this.checkEnabledAndRedirect(true);
        } else if (this.capabilitiesService.capabilities.stickyPagesEnabled) {
            const stickyPage = await this.getStickyPage();
            const shouldActivateRoute = !stickyPage || stickyPage === RoutePaths.OfficeHome || stickyPage === RoutePaths.OfficeHomeFallback;

            if (stickyPage === null) {
                logFeature({
                    featureName: KnownFeatureNames.Other,
                    eventName: STICKY_PAGE_REDIRECT_EVENT_NAME,
                    params: {
                        Success: false,
                        ErrorType: 'Other',
                        Error: 'Sticky Page Timeout'
                    }
                });
            } else {
                logFeature({
                    featureName: KnownFeatureNames.Other,
                    eventName: STICKY_PAGE_REDIRECT_EVENT_NAME,
                    params: {
                        Success: true,
                        Executed: !shouldActivateRoute
                    }
                });
            }

            return this.checkEnabledAndRedirect(shouldActivateRoute, stickyPage, route.queryParams);
        }

        return this.checkEnabledAndRedirect(true);
    }

    private getStickyTab(): RoutePaths | undefined {
        return !this.router.navigated ? this.localStorageService.get(BottomTrayComponent.LastBottomTrayTab) : undefined;
    }

    private getStickyPage(): Promise<string | undefined> {
        return Promise.race([
            new Promise<string>(async resolve => {
                if (this.router.navigated) {
                    return resolve(undefined);
                }

                try {
                    const foldersService = await import('@office-start/folders-service');
                    await foldersService.initializeFolders();
                    const folders = foldersService.getFoldersData();

                    if (folders) {
                        const stickyPageService = await import('@office-start/sticky-page-service');
                        const stickyPageCard = await stickyPageService.getStickyPage();

                        logFeature({
                            featureName: KnownFeatureNames.Other,
                            eventName: STICKY_PAGE_FETCH_EVENT_NAME,
                            params: {
                                Success: true
                            }
                        });

                        if (stickyPageCard?.urlPath) {
                            return resolve(stickyPageCard.urlPath.slice(1));
                        }
                    }
                } catch (error) {
                    logFeature({
                        featureName: KnownFeatureNames.Other,
                        eventName: STICKY_PAGE_FETCH_EVENT_NAME,
                        params: {
                            Success: false,
                            ErrorType: 'Other',
                            ErrorMessage: error
                        }
                    });
                }

                return resolve(undefined);
            }),
            new Promise<null>(resolve => {
                setTimeout(() => resolve(null), STICKY_PAGE_TIMEOUT);
            })
        ]);
    }
}
